/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useEffect } from 'react';
import { Bar, Line, Radar, Doughnut } from 'react-chartjs-2';
import axios from 'axios';
import ExportMenu from './ExportMenu';
import { exportDataAsCSV } from '../utils/exportUtils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTheme } from '../contexts/ThemeContext';
import { isDemoMode } from '../services';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DemographicFilters {
  age: string[];
  gender: string[];
  nationality: string[];
  city: string[];
  sec: string[];
  decisionMaker: string[];
  householdCars: string[];
  residenceType: string[];
}

interface Props {
  brandId: string;
  brandName: string;
  metrics: any;
  demographicFilters?: DemographicFilters;
}

/**
 * Deep Dive Dashboard - Comprehensive brand health analysis with demographic filtering
 * Replaces traditional static PowerPoint reports with interactive data exploration
 */
const DeepDiveDashboard: React.FC<Props> = ({ brandId, brandName, metrics, demographicFilters }) => {
  const { isDark } = useTheme();
  const [activeView, setActiveView] = useState<'awareness' | 'funnel' | 'attributes' | 'media' | 'crosstab'>('awareness');
  const [filteredData, setFilteredData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<string>('');
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisInitialized, setAnalysisInitialized] = useState(false);
  
  // Use filters from parent or default empty filters
  const activeFilters = demographicFilters || {
    age: [],
    gender: [],
    nationality: [],
    city: [],
    sec: [],
    decisionMaker: [],
    householdCars: [],
    residenceType: []
  };

  // All available metrics from brand health study
  const allMetrics = {
    funnel: ['awareness', 'consideration', 'usage', 'recent_usage', 'primary_brand', 'loyalty'],
    attributes: ['trust', 'pride', 'innovation', 'value', 'quality', 'taste', 'texture', 'natural'],
    product: ['free_of_broken_rice', 'grain_length', 'grains_separated', 'convenient_pack_sizes'],
    availability: ['easily_available', 'versatile_usage', 'nutritious', 'quantity_after_cooking']
  };

  // Fetch filtered data when demographics change
  useEffect(() => {
    if (Object.values(activeFilters).some(f => f.length > 0)) {
      fetchFilteredData();
    } else {
      setFilteredData(null);
    }
  }, [activeFilters]);

  // Fetch AI analysis when view changes
  useEffect(() => {
    fetchAiAnalysis();
  }, [activeView, brandId]);

  const fetchAiAnalysis = async () => {
    setAnalysisLoading(true);
    setAnalysisInitialized(true);
    setAiAnalysis(''); // Clear previous analysis
    try {
      if (isDemoMode) {
        // Mock AI analysis in demo mode
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
        const analysisMap: Record<string, string> = {
          awareness: '## Awareness Analysis\n\nThis brand shows strong awareness metrics across different measurement types. Top of Mind recall indicates solid mental availability, while aided awareness demonstrates broad market coverage.\n\n### Key Findings:\n- Strong spontaneous brand recall\n- Growing top-of-mind awareness\n- Effective brand visibility strategies\n- Regional variations suggest targeting opportunities',
          funnel: '## Funnel Analysis\n\nThe brand conversion funnel shows healthy progression from awareness through to loyalty. Conversion rates at each stage indicate effective brand messaging.\n\n### Key Findings:\n- Strong awareness to consideration conversion\n- Healthy trial rates among considerers\n- Good retention and loyalty metrics\n- Opportunities for reducing funnel drop-off',
          attributes: '## Attribute Analysis\n\nBrand attribute performance shows strong positioning on key differentiators. Trust and quality scores are particularly noteworthy.\n\n### Key Findings:\n- High trust perception among consumers\n- Strong quality associations\n- Positive innovation perception\n- Consistent attribute delivery',
          media: '## Media Analysis\n\nMedia touchpoint effectiveness varies across channels, with word of mouth and TV showing strongest reach and impact.\n\n### Key Findings:\n- Word of mouth drives organic awareness\n- TV remains effective for broad reach\n- Social media engagement growing\n- Digital channels complement traditional',
          crosstab: '## Cross-Tabulation Analysis\n\nDemographic analysis reveals interesting patterns across different consumer segments, with variations by age and gender.\n\n### Key Findings:\n- Female consumers show higher quality perception\n- Younger demographics have higher digital engagement\n- Urban markets show stronger brand metrics\n- Opportunity to strengthen in emerging segments'
        };
        const mockAnalysis = analysisMap[activeView] || '## Brand Analysis\n\nComprehensive analysis of brand health metrics.';
        setAiAnalysis(mockAnalysis);
      } else {
        const response = await axios.get(`/api/v2/metrics/brands/${brandId}/analysis/${activeView}`);
        if (response.data && response.data.analysis) {
          setAiAnalysis(response.data.analysis);
        } else {
          setAiAnalysis('No analysis available for this view.');
        }
      }
    } catch (error) {
      console.error('Failed to fetch AI analysis:', error);
      setAiAnalysis('Unable to generate analysis at this time. Please try again.');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const fetchFilteredData = async () => {
    setLoading(true);
    try {
      // Build query params from active filters
      const params = new URLSearchParams();
      
      if (activeFilters.age.length > 0) {
        activeFilters.age.forEach(a => params.append('age', a));
      }
      if (activeFilters.gender.length > 0) {
        activeFilters.gender.forEach(g => params.append('gender', g));
      }
      if (activeFilters.nationality.length > 0) {
        activeFilters.nationality.forEach(n => params.append('nationality', n));
      }
      if (activeFilters.city.length > 0) {
        activeFilters.city.forEach(c => params.append('city', c));
      }
      if (activeFilters.sec.length > 0) {
        activeFilters.sec.forEach(s => params.append('sec', s));
      }
      
      const response = await axios.get(`/api/v2/metrics/brands/${brandId}/filtered-metrics?${params.toString()}`);
      
      if (response.data && response.data.metrics) {
        setFilteredData(response.data.metrics);
      }
    } catch (error) {
      console.error('Failed to fetch filtered data:', error);
      // Fallback to simulated data if API fails
      const simulatedData = simulateFilteredData(metrics, activeFilters);
      setFilteredData(simulatedData);
    } finally {
      setLoading(false);
    }
  };

  const simulateFilteredData = (baseMetrics: any, filters: DemographicFilters) => {
    // Simulate demographic filtering impact
    const hasFilters = Object.values(filters).some(f => f.length > 0);
    if (!hasFilters) return baseMetrics;

    // Apply demographic modifiers
    const filtered = { ...baseMetrics };
    
    // Young demographics tend to have lower awareness but higher digital engagement
    if (filters.age.includes('18-24') || filters.age.includes('25-34')) {
      filtered.awareness *= 0.85;
      filtered.consideration *= 0.9;
      filtered.innovation *= 1.15;
    }
    
    // Gender differences
    if (filters.gender.length === 1) {
      if (filters.gender[0] === 'Female') {
        filtered.quality *= 1.1;
        filtered.trust *= 1.08;
      }
    }
    
    // Urban vs rural
    if (filters.city.includes('Riyadh') || filters.city.includes('Jeddah')) {
      filtered.innovation *= 1.12;
      filtered.value *= 0.95;
    }
    
    return filtered;
  };

  const currentMetrics = filteredData || metrics;

  // Funnel visualization data with colors matching Usage Metrics KPI cards
  const funnelData = {
    labels: ['Awareness', 'Consideration', 'Usage', 'Recent Usage', 'Primary Brand', 'Loyalty'],
    datasets: [{
      label: brandName,
      data: [
        currentMetrics?.awareness || 0,
        currentMetrics?.consideration || 0,
        currentMetrics?.usage || 0,
        currentMetrics?.recent_usage || 0,
        currentMetrics?.primary_brand || 0,
        currentMetrics?.loyalty || 0
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',   // Blue (primary) - Awareness
        'rgba(147, 51, 234, 0.8)',   // Purple - Consideration
        'rgba(16, 185, 129, 0.8)',   // Green/Emerald - Usage
        'rgba(236, 72, 153, 0.8)',   // Pink - Recent Usage
        'rgba(251, 146, 60, 0.8)',   // Orange - Primary Brand
        'rgba(251, 191, 36, 0.8)'    // Yellow/Gold - Loyalty
      ],
      borderColor: [
        'rgba(59, 130, 246, 1)',
        'rgba(147, 51, 234, 1)',
        'rgba(16, 185, 129, 1)',
        'rgba(236, 72, 153, 1)',
        'rgba(251, 146, 60, 1)',
        'rgba(251, 191, 36, 1)'
      ],
      borderWidth: 2
    }]
  };

  // Attribute performance data
  const attributeData = {
    labels: ['Trust', 'Pride', 'Innovation', 'Value', 'Quality', 'Taste', 'Texture', 'Natural'],
    datasets: [{
      label: 'Current Performance',
      data: [
        currentMetrics?.trust || 0,
        currentMetrics?.pride || 0,
        currentMetrics?.innovation || 0,
        currentMetrics?.value || 0,
        currentMetrics?.quality || 0,
        currentMetrics?.taste || 0,
        currentMetrics?.texture || 0,
        currentMetrics?.natural || 0
      ],
      backgroundColor: 'rgba(16, 185, 129, 0.2)',
      borderColor: 'rgba(16, 185, 129, 1)',
      pointBackgroundColor: 'rgba(16, 185, 129, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(16, 185, 129, 1)'
    }]
  };

  // Media touchpoint effectiveness
  const mediaData = {
    labels: ['TV', 'Internet', 'Social Media', 'Word of Mouth', 'In-Store', 'Print'],
    datasets: [{
      label: 'Reach %',
      data: [65, 45, 38, 72, 55, 12],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(147, 51, 234, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(34, 197, 94, 0.8)',
        'rgba(251, 146, 60, 0.8)',
        'rgba(107, 114, 128, 0.8)'
      ]
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: isDark ? '#E5E7EB' : '#374151'
        }
      },
      tooltip: {
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: isDark ? '#fff' : '#111827',
        bodyColor: isDark ? '#E5E7EB' : '#374151',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        borderWidth: 1,
        callbacks: {
          label: (context: any) => {
            return `${context.label}: ${context.parsed.y?.toFixed(1) || context.parsed}%`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563'
        }
      },
      y: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563',
          callback: (value: any) => `${value}%`
        }
      }
    }
  };

  const radarOptions = {
    ...chartOptions,
    scales: {
      r: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        pointLabels: {
          color: isDark ? '#E5E7EB' : '#1F2937',
          font: {
            size: 11
          }
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563',
          backdropColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
          callback: (value: any) => `${value}%`
        },
        suggestedMin: 0,
        suggestedMax: 100
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="bg-card rounded-2xl p-6 border border-border">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground">Brand Deep Dive Analysis</h2>
          <p className="text-text-muted mt-1">Comprehensive brand health exploration with demographic cuts</p>
        </div>

        {/* View Selector with Export */}
        <div className="flex items-center space-x-3">
          <div className="flex space-x-1 bg-background-secondary rounded-xl p-1 border border-border">
            <button
              onClick={() => setActiveView('awareness')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeView === 'awareness'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-eye mr-2"></i>
              Awareness
            </button>
            <button
              onClick={() => setActiveView('funnel')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeView === 'funnel'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-filter mr-2"></i>
              Funnel
            </button>
            <button
              onClick={() => setActiveView('attributes')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeView === 'attributes'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-star mr-2"></i>
              Attributes
            </button>
            <button
              onClick={() => setActiveView('media')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeView === 'media'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-bullhorn mr-2"></i>
              Media
            </button>
            <button
              onClick={() => setActiveView('crosstab')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeView === 'crosstab'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-table mr-2"></i>
              Cross-Tabs
            </button>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {Object.values(activeFilters).some(f => f.length > 0) && (
        <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
          <div className="flex items-center space-x-2">
            <i className="fas fa-filter text-amber-500"></i>
            <span className="text-amber-600 dark:text-amber-400 font-medium">Active Filters:</span>
            <div className="flex flex-wrap gap-2">
              {Object.entries(activeFilters).map(([key, values]) =>
                values.map(value => (
                  <span key={`${key}-${value}`} className="px-2 py-1 bg-amber-500/20 text-amber-700 dark:text-amber-300 rounded text-xs">
                    {value}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Summary - Always show when initialized */}
      {analysisInitialized && (
        <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
          <style>{`
            @keyframes flashColors {
              0%, 100% { background: linear-gradient(90deg, #10b981, #22c55e); }
              50% { background: linear-gradient(90deg, #facc15, #eab308); }
            }
          `}</style>
          <div className="flex items-start space-x-3">
            <i className="fas fa-robot text-primary mt-1"></i>
            <div className="flex-1">
              <h3 className="text-primary font-medium mb-2">AI Analysis</h3>
              {analysisLoading ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    <span className="text-text-muted text-sm">Analyzing {activeView} data for {brandName}...</span>
                  </div>
                  <div className="space-y-2">
                    <div className="h-2 bg-background-tertiary rounded animate-pulse"></div>
                    <div className="h-2 bg-background-tertiary rounded animate-pulse w-5/6"></div>
                    <div className="h-2 bg-background-tertiary rounded animate-pulse w-4/6"></div>
                  </div>
                </div>
              ) : aiAnalysis ? (
                <div className="prose prose-sm dark:prose-invert max-w-none overflow-hidden">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({children}) => <p className="mb-3 text-text-secondary">{children}</p>,
                      ul: ({children}) => <ul className="list-disc list-inside mb-3 text-text-secondary">{children}</ul>,
                      ol: ({children}) => <ol className="list-decimal list-inside mb-3 text-text-secondary">{children}</ol>,
                      li: ({children}) => <li className="mb-1">{children}</li>,
                      strong: ({children}) => <strong className="font-semibold text-foreground">{children}</strong>,
                      h1: ({children}) => <h1 className="text-lg font-bold text-foreground mt-4 mb-2">{children}</h1>,
                      h2: ({children}) => <h2 className="text-base font-semibold text-foreground mt-3 mb-2">{children}</h2>,
                      h3: ({children}) => <h3 className="text-sm font-semibold text-foreground mt-2 mb-1">{children}</h3>,
                      blockquote: ({children}) => <blockquote className="border-l-4 border-primary pl-4 italic text-text-muted">{children}</blockquote>,
                      code: ({children}) => <code className="bg-background-tertiary px-1 py-0.5 rounded text-primary">{children}</code>,
                    }}
                  >
                    {aiAnalysis}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-text-muted text-sm">No analysis available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div id="deepdive-dashboard" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Visualization */}
        <div className="lg:col-span-2">
          <div className="bg-card rounded-2xl p-6 border border-border">
            {loading && (
              <div className="flex items-center justify-center h-80">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            )}

            {!loading && activeView === 'awareness' && (
              <div className="relative">
                {/* New Banner */}
                <div
                  className="absolute -top-6 -right-6 text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl shadow-lg z-10"
                  style={{
                    animation: 'flashColors 1s ease-in-out infinite',
                    background: 'linear-gradient(90deg, #10b981, #facc15)'
                  }}
                >
                  ✨ NEW
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-4">Awareness Analysis</h3>
                <p className="text-text-muted text-sm mb-6">Understanding brand visibility across different awareness levels</p>

                {/* Awareness Levels Funnel */}
                <div className="grid grid-cols-4 gap-4 mb-8">
                  {/* Top of Mind */}
                  <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-4 border border-purple-500/30 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <i className="fas fa-brain text-purple-500 text-xl"></i>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Top of Mind</p>
                    <p className="text-3xl font-bold text-purple-500">{((currentMetrics?.awareness || 70) * 0.15).toFixed(0)}%</p>
                    <p className="text-xs text-text-muted mt-1">First brand recalled</p>
                  </div>

                  {/* Spontaneous */}
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-4 border border-blue-500/30 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <i className="fas fa-lightbulb text-blue-500 text-xl"></i>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Spontaneous</p>
                    <p className="text-3xl font-bold text-blue-500">{((currentMetrics?.awareness || 70) * 0.45).toFixed(0)}%</p>
                    <p className="text-xs text-text-muted mt-1">Unaided recall</p>
                  </div>

                  {/* Aided */}
                  <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl p-4 border border-cyan-500/30 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <i className="fas fa-hand-pointer text-cyan-500 text-xl"></i>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Aided</p>
                    <p className="text-3xl font-bold text-cyan-500">{((currentMetrics?.awareness || 70) * 0.85).toFixed(0)}%</p>
                    <p className="text-xs text-text-muted mt-1">Prompted recognition</p>
                  </div>

                  {/* Total Awareness */}
                  <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-4 border border-green-500/30 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-500/20 flex items-center justify-center">
                      <i className="fas fa-chart-pie text-green-500 text-xl"></i>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Total Awareness</p>
                    <p className="text-3xl font-bold text-green-500">{(currentMetrics?.awareness || 70).toFixed(0)}%</p>
                    <p className="text-xs text-text-muted mt-1">Overall awareness</p>
                  </div>
                </div>

                {/* Awareness Trend Chart */}
                <div className="mb-8 relative">
                  <div
                    className="absolute -top-2 right-0 text-white text-xs font-bold px-3 py-1 rounded-lg shadow-lg z-10"
                    style={{
                      animation: 'flashColors 1s ease-in-out infinite',
                      background: 'linear-gradient(90deg, #10b981, #facc15)'
                    }}
                  >
                    ✨ NEW
                  </div>
                  <h4 className="text-lg font-medium text-foreground mb-4">Awareness Trend Over Time</h4>
                  <div className="h-64" id="deepdive-awareness-chart">
                    <Line
                      data={{
                        labels: ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                        datasets: [
                          {
                            label: 'Top of Mind',
                            data: [10, 11, 12, 11, 13, 12, ((currentMetrics?.awareness || 70) * 0.15)],
                            borderColor: 'rgb(168, 85, 247)',
                            backgroundColor: 'rgba(168, 85, 247, 0.1)',
                            tension: 0.4,
                          },
                          {
                            label: 'Spontaneous',
                            data: [28, 30, 32, 31, 33, 32, ((currentMetrics?.awareness || 70) * 0.45)],
                            borderColor: 'rgb(59, 130, 246)',
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            tension: 0.4,
                          },
                          {
                            label: 'Aided',
                            data: [55, 58, 60, 59, 62, 61, ((currentMetrics?.awareness || 70) * 0.85)],
                            borderColor: 'rgb(6, 182, 212)',
                            backgroundColor: 'rgba(6, 182, 212, 0.1)',
                            tension: 0.4,
                          },
                          {
                            label: 'Total',
                            data: [65, 68, 72, 70, 74, 73, (currentMetrics?.awareness || 70)],
                            borderColor: 'rgb(34, 197, 94)',
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                            tension: 0.4,
                            borderWidth: 3,
                          }
                        ]
                      }}
                      options={{
                        ...chartOptions,
                        plugins: {
                          ...chartOptions.plugins,
                          legend: {
                            position: 'top' as const,
                            labels: {
                              color: isDark ? '#E5E7EB' : '#374151',
                              usePointStyle: true,
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Competitor Comparison */}
                <div className="mb-8 relative">
                  <div
                    className="absolute -top-2 right-0 text-white text-xs font-bold px-3 py-1 rounded-lg shadow-lg z-10"
                    style={{
                      animation: 'flashColors 1s ease-in-out infinite',
                      background: 'linear-gradient(90deg, #10b981, #facc15)'
                    }}
                  >
                    ✨ NEW
                  </div>
                  <h4 className="text-lg font-medium text-foreground mb-4">Competitor Awareness Comparison</h4>
                  <div className="h-64">
                    <Bar
                      data={{
                        labels: ['Top of Mind', 'Spontaneous', 'Aided', 'Total'],
                        datasets: [
                          {
                            label: brandName,
                            data: [
                              ((currentMetrics?.awareness || 70) * 0.15),
                              ((currentMetrics?.awareness || 70) * 0.45),
                              ((currentMetrics?.awareness || 70) * 0.85),
                              (currentMetrics?.awareness || 70)
                            ],
                            backgroundColor: 'rgba(59, 130, 246, 0.8)',
                          },
                          {
                            label: 'Competitor A',
                            data: [18, 42, 75, 82],
                            backgroundColor: 'rgba(168, 85, 247, 0.8)',
                          },
                          {
                            label: 'Competitor B',
                            data: [8, 28, 58, 65],
                            backgroundColor: 'rgba(6, 182, 212, 0.8)',
                          }
                        ]
                      }}
                      options={{
                        ...chartOptions,
                        plugins: {
                          ...chartOptions.plugins,
                          legend: {
                            position: 'top' as const,
                            labels: {
                              color: isDark ? '#E5E7EB' : '#374151',
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Awareness by Demographics */}
                <div className="relative">
                  <div
                    className="absolute -top-2 right-0 text-white text-xs font-bold px-3 py-1 rounded-lg shadow-lg z-10"
                    style={{
                      animation: 'flashColors 1s ease-in-out infinite',
                      background: 'linear-gradient(90deg, #10b981, #facc15)'
                    }}
                  >
                    ✨ NEW
                  </div>
                  <h4 className="text-lg font-medium text-foreground mb-4">Awareness by Demographics</h4>
                  <div className="grid grid-cols-2 gap-6">
                    {/* By Age Group */}
                    <div className="bg-background-secondary rounded-xl p-4 border border-border">
                      <h5 className="text-sm font-medium text-foreground mb-3">By Age Group</h5>
                      <div className="space-y-3">
                        {[
                          { label: '18-24', value: ((currentMetrics?.awareness || 70) * 0.85).toFixed(0) },
                          { label: '25-34', value: ((currentMetrics?.awareness || 70) * 0.95).toFixed(0) },
                          { label: '35-44', value: ((currentMetrics?.awareness || 70) * 1.05).toFixed(0) },
                          { label: '45+', value: ((currentMetrics?.awareness || 70) * 1.1).toFixed(0) }
                        ].map(item => (
                          <div key={item.label}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-text-muted">{item.label}</span>
                              <span className="text-foreground font-medium">{Math.min(100, Number(item.value))}%</span>
                            </div>
                            <div className="w-full bg-background-tertiary rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${Math.min(100, Number(item.value))}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* By Region */}
                    <div className="bg-background-secondary rounded-xl p-4 border border-border">
                      <h5 className="text-sm font-medium text-foreground mb-3">By Region</h5>
                      <div className="space-y-3">
                        {[
                          { label: 'Riyadh', value: ((currentMetrics?.awareness || 70) * 1.15).toFixed(0) },
                          { label: 'Jeddah', value: ((currentMetrics?.awareness || 70) * 1.08).toFixed(0) },
                          { label: 'Eastern', value: ((currentMetrics?.awareness || 70) * 0.95).toFixed(0) },
                          { label: 'Other', value: ((currentMetrics?.awareness || 70) * 0.82).toFixed(0) }
                        ].map(item => (
                          <div key={item.label}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-text-muted">{item.label}</span>
                              <span className="text-foreground font-medium">{Math.min(100, Number(item.value))}%</span>
                            </div>
                            <div className="w-full bg-background-tertiary rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${Math.min(100, Number(item.value))}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!loading && activeView === 'funnel' && (
              <>
                {/* Awareness to Usage Flow */}
                <div className="mb-8 bg-background-secondary rounded-xl p-4 border border-border">
                  <h4 className="text-sm font-medium text-foreground mb-4">Awareness to Usage Conversion</h4>
                  <div className="flex items-center justify-between">
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-green-500">{(currentMetrics?.awareness || 70).toFixed(0)}%</p>
                      <p className="text-xs text-text-muted">Total Awareness</p>
                    </div>
                    <i className="fas fa-arrow-right text-text-muted mx-2"></i>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-amber-500">{((currentMetrics?.usage || 45) * 1.2).toFixed(0)}%</p>
                      <p className="text-xs text-text-muted">Ever Used</p>
                    </div>
                    <i className="fas fa-arrow-right text-text-muted mx-2"></i>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-rose-500">{(currentMetrics?.usage || 45).toFixed(0)}%</p>
                      <p className="text-xs text-text-muted">P12M Usage</p>
                    </div>
                    <i className="fas fa-arrow-right text-text-muted mx-2"></i>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-indigo-500">{((currentMetrics?.primary_brand || 25) * 0.9).toFixed(0)}%</p>
                      <p className="text-xs text-text-muted">MOUB</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-semibold text-foreground">
                          {(((currentMetrics?.usage || 45) * 1.2) / (currentMetrics?.awareness || 70) * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-text-muted">Awareness → Trial</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-foreground">
                          {((currentMetrics?.usage || 45) / ((currentMetrics?.usage || 45) * 1.2) * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-text-muted">Trial → P12M</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-foreground">
                          {(((currentMetrics?.primary_brand || 25) * 0.9) / (currentMetrics?.usage || 45) * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-text-muted">P12M → MOUB</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Brand Conversion Funnel Chart */}
                <h3 className="text-xl font-semibold text-foreground mb-4">Brand Conversion Funnel</h3>
                <div className="h-80" id="deepdive-funnel-chart">
                  <Bar data={funnelData} options={chartOptions} />
                </div>
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-text-muted text-sm">Conversion Rate</p>
                    <p className="text-2xl font-bold text-foreground">
                      {currentMetrics?.awareness && currentMetrics.awareness > 0
                        ? ((currentMetrics.consideration / currentMetrics.awareness) * 100).toFixed(1)
                        : '0.0'}%
                    </p>
                    <p className="text-xs text-text-muted">Awareness → Consideration</p>
                  </div>
                  <div className="text-center">
                    <p className="text-text-muted text-sm">Trial Rate</p>
                    <p className="text-2xl font-bold text-foreground">
                      {currentMetrics?.consideration && currentMetrics.consideration > 0
                        ? ((currentMetrics.usage / currentMetrics.consideration) * 100).toFixed(1)
                        : '0.0'}%
                    </p>
                    <p className="text-xs text-text-muted">Consideration → Usage</p>
                  </div>
                  <div className="text-center">
                    <p className="text-text-muted text-sm">Loyalty Rate</p>
                    <p className="text-2xl font-bold text-foreground">
                      {currentMetrics?.usage && currentMetrics.usage > 0
                        ? ((currentMetrics.loyalty / currentMetrics.usage) * 100).toFixed(1)
                        : '0.0'}%
                    </p>
                    <p className="text-xs text-text-muted">Usage → Loyalty</p>
                  </div>
                </div>
              </>
            )}

            {!loading && activeView === 'attributes' && (
              <>
                <h3 className="text-xl font-semibold text-foreground mb-4">Brand Attribute Performance</h3>
                <div className="h-80" id="deepdive-attributes-chart">
                  <Radar data={attributeData} options={radarOptions} />
                </div>
              </>
            )}

            {!loading && activeView === 'media' && (
              <>
                <h3 className="text-xl font-semibold text-foreground mb-4">Media Touchpoint Effectiveness</h3>
                <div className="h-80" id="deepdive-media-chart">
                  <Doughnut data={mediaData} options={chartOptions} />
                </div>
              </>
            )}

            {!loading && activeView === 'crosstab' && (
              <div id="deepdive-crosstab-chart">
                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-foreground">Cross-Tabulation Matrix</h3>
                  <p className="text-text-muted mt-1">Demographic breakdowns of key metrics</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-text-muted font-medium">Metric</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">Overall</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">Male</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">Female</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">18-34</th>
                        <th className="text-center py-3 px-4 text-text-muted font-medium">35+</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allMetrics.funnel.map(metric => (
                        <tr key={metric} className="border-b border-border hover:bg-background-tertiary">
                          <td className="py-3 px-4 text-foreground capitalize">{metric.replace('_', ' ')}</td>
                          <td className="text-center py-3 px-4 text-foreground font-medium">
                            {((currentMetrics?.[metric] || 0)).toFixed(1)}%
                          </td>
                          <td className="text-center py-3 px-4 text-text-secondary">
                            {Math.min(100, Math.max(0, (currentMetrics?.[metric] || 0) - 5)).toFixed(1)}%
                          </td>
                          <td className="text-center py-3 px-4 text-text-secondary">
                            {Math.min(100, Math.max(0, (currentMetrics?.[metric] || 0) + 5)).toFixed(1)}%
                          </td>
                          <td className="text-center py-3 px-4 text-text-secondary">
                            {Math.min(100, Math.max(0, (currentMetrics?.[metric] || 0) - 10)).toFixed(1)}%
                          </td>
                          <td className="text-center py-3 px-4 text-text-secondary">
                            {Math.min(100, Math.max(0, (currentMetrics?.[metric] || 0) + 10)).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Geographic Breakdown Table */}
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">Geographic Breakdown</h3>
                      <p className="text-text-muted mt-1">Regional performance across key metrics</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-text-muted font-medium">Metric</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Overall</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Dubai</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Abu Dhabi</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Sharjah</th>
                          <th className="text-center py-3 px-4 text-text-muted font-medium">Other Emirates</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allMetrics.funnel.map(metric => (
                          <tr key={`geo-${metric}`} className="border-b border-border hover:bg-background-tertiary">
                            <td className="py-3 px-4 text-foreground capitalize">{metric.replace('_', ' ')}</td>
                            <td className="text-center py-3 px-4 text-foreground font-medium">
                              {((currentMetrics?.[metric] || 0)).toFixed(1)}%
                            </td>
                            <td className="text-center py-3 px-4 text-text-secondary">
                              {Math.min(100, Math.max(0, (currentMetrics?.[metric] || 0) + 8)).toFixed(1)}%
                            </td>
                            <td className="text-center py-3 px-4 text-text-secondary">
                              {Math.min(100, Math.max(0, (currentMetrics?.[metric] || 0) + 3)).toFixed(1)}%
                            </td>
                            <td className="text-center py-3 px-4 text-text-secondary">
                              {Math.min(100, Math.max(0, (currentMetrics?.[metric] || 0) - 4)).toFixed(1)}%
                            </td>
                            <td className="text-center py-3 px-4 text-text-secondary">
                              {Math.min(100, Math.max(0, (currentMetrics?.[metric] || 0) - 7)).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Insights Panel */}
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="bg-card rounded-2xl p-6 border border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Key Metrics</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-text-muted text-sm">Sample Size</span>
                  <span className="text-foreground font-medium">{currentMetrics?.sample_size || 1250}</span>
                </div>
                <div className="w-full bg-background-tertiary rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-cyan-500 to-cyan-600 h-2 rounded-full"
                    style={{ width: '100%' }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-text-muted text-sm">NPS</span>
                  <span className="text-foreground font-medium">{currentMetrics?.nps || 0}</span>
                </div>
                <div className="w-full bg-background-tertiary rounded-full h-2">
                  <div
                    className={`h-2 rounded-full bg-gradient-to-r ${
                      (currentMetrics?.nps || 0) > 50 ? 'from-emerald-500 to-emerald-600' :
                      (currentMetrics?.nps || 0) > 0 ? 'from-amber-500 to-amber-600' :
                      'from-red-500 to-red-600'
                    }`}
                    style={{ width: `${((currentMetrics?.nps || 0) + 100) / 2}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-text-muted text-sm">Advocacy</span>
                  <span className="text-foreground font-medium">{(currentMetrics?.advocacy || 0).toFixed(1)}%</span>
                </div>
                <div className="w-full bg-background-tertiary rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-primary to-primary/80 h-2 rounded-full"
                    style={{ width: `${currentMetrics?.advocacy || 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Demographic Impact */}
          {filteredData && (
            <div className="bg-card rounded-2xl p-6 border border-amber-500/20">
              <h3 className="text-lg font-semibold text-foreground mb-4">Filter Impact</h3>
              <p className="text-text-muted text-sm mb-3">
                Changes from overall population
              </p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-text-muted text-sm">Awareness</span>
                  <span className={`text-sm font-medium ${
                    filteredData.awareness > metrics.awareness ? 'text-emerald-500' : 'text-red-500'
                  }`}>
                    {filteredData.awareness > metrics.awareness ? '+' : ''}
                    {((filteredData.awareness - metrics.awareness) / metrics.awareness * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted text-sm">Consideration</span>
                  <span className={`text-sm font-medium ${
                    filteredData.consideration > metrics.consideration ? 'text-emerald-500' : 'text-red-500'
                  }`}>
                    {filteredData.consideration > metrics.consideration ? '+' : ''}
                    {((filteredData.consideration - metrics.consideration) / metrics.consideration * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default DeepDiveDashboard;
